/**
 * Reads the org's scheduling defaults off the `ahoy_org` CRM entity and
 * returns them as booking-type form values.
 *
 * The defaults are CRM properties, so they come through the object store — the
 * same path Settings → Meeting assistant writes them on. Callers get `{}` until
 * the org entity resolves, which is why the hook belongs on a page (mounted
 * early) rather than inside the create slideout (mounted on click).
 */

import { useMemo } from 'react';

import type { EntityTypeName } from '@/lib/crm/core/types';
import { useCrmObject } from '@/lib/crm/store';

import { mapOrgDefaultsToBookingTypeValues } from './orgSchedulingDefaults';

export interface OrgSchedulingDefaultsResult {
  defaults: Record<string, unknown>;
  // Callers MUST gate on isLoading/error before seeding a create form from
  // `defaults` — otherwise a cold-cache render seeds from an empty object
  // before the org entity fetch resolves. See MeetingTypesPage.tsx.
  isLoading: boolean;
  error: Error | null;
}

export function useOrgSchedulingDefaults(
  orgId: number | null | undefined,
  orgEntityId: string | null | undefined
): OrgSchedulingDefaultsResult {
  const {
    object: orgEntity,
    isLoading,
    error,
  } = useCrmObject(
    orgId ?? 0,
    'ahoy_org' as EntityTypeName,
    orgEntityId ?? undefined
  );

  const defaults = useMemo(
    () => mapOrgDefaultsToBookingTypeValues(orgEntity),
    [orgEntity]
  );

  return { defaults, isLoading, error };
}
